import { ActionTypes, } from "../../../OrderFlow/OrderFlowProvider/OrderFlowActionTypes";
import { useOrderFlow } from "../../../OrderFlow/OrderFlowProvider/OrderFlowContext";
import { NameDisplay } from "../../../OrderFlow/OrderFlowProvider/OrderFlowTypes";
import { getOriginalCounterPart } from "../ReactMultiCarousel/utils";
import { checkReplacementsAndConflicts } from "../../../OrderFlow/hooks/RecipeSelection/recipeSelectionUtils";
export var SortOptions;
(function (SortOptions) {
    SortOptions[SortOptions["ASC_ABC"] = 1] = "ASC_ABC";
    SortOptions[SortOptions["DESC_ABC"] = 2] = "DESC_ABC";
    SortOptions[SortOptions["DESC_COST"] = 3] = "DESC_COST";
    SortOptions[SortOptions["ASC_COST"] = 4] = "ASC_COST";
    SortOptions[SortOptions["SUGGESTED"] = 5] = "SUGGESTED";
})(SortOptions || (SortOptions = {}));
export var SortingText;
(function (SortingText) {
    SortingText["SORT_BY"] = "Sort by:";
    SortingText["DESC_ABC"] = "Z to A";
    SortingText["ASC_ABC"] = "A to Z";
    SortingText["ASC_COST"] = "Cost: Low to High";
    SortingText["DESC_COST"] = "Cost: High to Low";
    SortingText["SUGGESTED"] = "Suggested";
})(SortingText || (SortingText = {}));
// Options for the sort-by input.
export const sortOptions = [
    // Alphabetical sorting.
    { value: 1, label: SortingText.ASC_ABC },
    { value: 2, label: SortingText.DESC_ABC },
    // Cost sorting.
    { value: 3, label: SortingText.DESC_COST },
    { value: 4, label: SortingText.ASC_COST },
    // Suggested recipe sorting.
    { value: 5, label: SortingText.SUGGESTED },
];
/**
 * When the sort method changes, we move to the first slide.
 * @param carouselRef
 */
const goToFirstSlide = (carouselRef) => {
    if (carouselRef && carouselRef.current) {
        const newIndex = getOriginalCounterPart(0, carouselRef.current.state, carouselRef.current.props.children);
        carouselRef.current.goToSlide(newIndex);
    }
};
/**
 * Generic change func for both mobile and desktop. This reduces
 * the change handler code while maintaining the separation of the
 * two different events (mobile uses a menu, desktop uses a select).
 * @param setSortMethod
 * @param dispatch
 * @param isRecipeModule
 * @param carouselRef
 * @param value pulled from event.target or event.currentTarget
 */
const handleSortChange = (setSortMethod, dispatch, isRecipeModule, carouselRef, value) => {
    setSortMethod((_) => value);
    goToFirstSlide(carouselRef);
    if (isRecipeModule) {
        dispatch({ type: ActionTypes.SET_RECIPE_SORT_OPTION, payload: value });
    }
    else {
        dispatch({ type: ActionTypes.SET_PRODUCT_SORT_OPTION, payload: value });
    }
};
// Mobile menu close function.
export const handleMenuClose = (setAnchorEl) => () => {
    setAnchorEl(null);
};
// Mobile menu open function.
export const handleMenuClick = (setAnchorEl) => (event) => {
    setAnchorEl(event.currentTarget);
};
// Desktop select input function.
export const handleDesktopSortChange = (setSortMethod, dispatch, isRecipeModule, carouselRef) => (event, _) => {
    handleSortChange(setSortMethod, dispatch, isRecipeModule, carouselRef, Number(event.target.value));
};
// Mobile menu select function.
export const handleMobileSortChange = (setSortMethod, dispatch, handleMenuClose, setAnchorEl, isRecipeModule, carouselRef) => (event) => {
    handleSortChange(setSortMethod, dispatch, isRecipeModule, carouselRef, Number(event.currentTarget.value));
    handleMenuClose(setAnchorEl);
};
/**
 * Helper function for the sortingMethod function.
 * @param item
 * @param useShortName
 * @returns item name string
 */
const itemNameToUse = (item, useShortName) => useShortName && item.props.itemShortName ? item.props.itemShortName : item.props.itemName;
/**
 * Removes commas and other non-numeric characters from a numeric string for comparisons.
 */
const numericStringRegex = /[^0-9\.]+/g;
/**
 * Helper function for the sortingMethod function.
 * The item price object text is a cost-as-string, so we use locale compare with the isNumeric option: true.
 * This function handles the case when one or more items are expected to have a price but do not.
 * We return a "0.00" to adhere to the locale compare function if we do not have/cannot access the item price object text.
 * Stringed values are stripped of non-numeric characters before comparing (E.g., commas and currency signs, but not decimal points).
 * @param item
 * @returns string cost value or "$0.00"
 */
const costValueToUse = (item) => item.props.itemPriceObject.text ? item.props.itemPriceObject.text.replace(numericStringRegex, "") : "0.00";
/**
 * Helper function for the sortingMethod function.
 * The changeRank determines how to sort recipes. The lower the number the better.
 * This function handles the case when one or more items are expected to have a changeRank but do not.
 * When showing compatibility symbols they should be sorted: MATCH, MODIFIED, CONFLICT. To acheive this we
 * add the value to the changeRank.
 * We return a "0" to adhere to the locale compare function if we do not have/cannot access the changeRank.
 * @param item
 * @returns changeRank value or "0"
 */
const changeRankValueToUse = (item) => { var _a; return item.props.changeRank >= 0 ? ((_a = item.props.changeRank + item.props.compatibilityType) !== null && _a !== void 0 ? _a : 0).toString() : "0"; };
export const recipeChangeRankValueToUse = (recipe, shouldReplace, omissions) => {
    var _a;
    const { compatibilityType } = checkReplacementsAndConflicts(recipe.RecipeIngredientActions, shouldReplace, recipe, omissions);
    return recipe.ChangeRank >= 0 ? ((_a = recipe.ChangeRank + compatibilityType) !== null && _a !== void 0 ? _a : 0).toString() : "0";
};
/**
 * @param sortMethod A numerical value corresponding to the sort method to be used.
 * { value: 1, label: `A to Z` },
 * { value: 2, label: `Z to A` },
 * { value: 3, label: `Cost: High to Low` },
 * { value: 4, label: `Cost: Low to High` },
 * { value: 5, label: `Suggested` },
 * @returns Helper function to be used for the item cards sorting function, passed directly to the sort method before mapping the display items.
 */
export const sortingFunction = (sortMethod) => {
    const { store } = useOrderFlow();
    const useShortName = store.model.ProductAndRecipeCardLayoutSettings.DisplayNameType == NameDisplay.ShortName;
    switch (sortMethod) {
        case 1: // { value: 1, label: `A to Z` }
            return (a, b) => itemNameToUse(a, useShortName).localeCompare(itemNameToUse(b, useShortName));
        case 2: // { value: 2, label: `Z to A` }
            return (a, b) => itemNameToUse(b, useShortName).localeCompare(itemNameToUse(a, useShortName));
        case 3: // { value: 3, label: `Cost: High to Low` }
            return (a, b) => costValueToUse(b).localeCompare(costValueToUse(a), undefined, { numeric: true });
        case 4: // { value: 4, label: `Cost: Low to High` }
            return (a, b) => costValueToUse(a).localeCompare(costValueToUse(b), undefined, { numeric: true });
        case 5: // { value: 5, label: `Suggested` }
            return (a, b) => changeRankValueToUse(a).localeCompare(changeRankValueToUse(b), undefined, { numeric: true });
        default: // { value: 1, label: `A to Z` }
            return (a, b) => itemNameToUse(a, useShortName).localeCompare(itemNameToUse(b, useShortName));
    }
};
/**
 * @param isRecipeModule
 * @param enableCostSorting
 * @param recipeSortOption
 * @param productSortOption
 * @param defaultToSuggested
 * @returns Returns the sort option based on the current module, the sort options saved in the cart and the module configurations
 */
export const getSortOption = (isRecipeModule, enableCostSorting, recipeSortOption, productSortOption, defaultToSuggested, sequenceNumber) => {
    var _a;
    const { store } = useOrderFlow();
    const moduleConfig = (_a = store.model.Flow.FlowModules.find((mod) => mod.Sequence === sequenceNumber && mod.Page === store.cart.currentPage)) === null || _a === void 0 ? void 0 : _a.ModuleConfig;
    let defaultSortOption = moduleConfig.DefaultSortOption ? moduleConfig.DefaultSortOption : 5;
    let defaultOption = defaultSortOption ? defaultSortOption : SortOptions.ASC_ABC;
    if (isRecipeModule) {
        if (recipeSortOption) {
            defaultOption = recipeSortOption;
        }
        //This prevents the sort option from being set to cost if cost sorting is disabled and cost sort option is saved in the cart
        if (!enableCostSorting &&
            (recipeSortOption === SortOptions.ASC_COST || recipeSortOption === SortOptions.DESC_COST)) {
            defaultOption = SortOptions.ASC_ABC;
        }
        //If we default to suggested and we DONT have a saved sort option, use suggested sort
        if (defaultToSuggested && !recipeSortOption) {
            defaultOption = SortOptions.SUGGESTED;
        }
    }
    else {
        if (productSortOption)
            defaultOption = productSortOption;
    }
    return defaultOption;
};
